import {
  AnalyticsFilterType,
  CartData,
  CreateCouponRequest,
  CreatePayoutRequest,
  DeliveryData,
  KitchenRegistrationStep1,
  NotificationsListResponse,
  OrderData,
  OrderParams,
  OrdersResponse,
  PaymentData,
  PaymentMethod,
  PayoutStatusType,
  RestaurantParams,
  ReviewData,
  StaticPageSlug,
  TransactionType,
  UnreadCountResponse,
  UserData,
} from "../utils/types";
import { mapAnalyticsToDashboard } from "../utils/analytics";
import apiClient from "./apiClient";

// User/Auth API calls
export const authService = {
  login: (email: string, password: string) =>
    apiClient.post("/auth/login", { email, password }),

  register: (userData: UserData) => apiClient.post("/auth/register", userData),

  logout: () => apiClient.post("/auth/logout"),

  getCurrentUser: () => apiClient.get("/auth/me"),

  updateProfile: (userData: UserData) =>
    apiClient.put("/users/profile", userData),
};

// Restaurant API calls
export const restaurantService = {
  getAllRestaurants: (params?: RestaurantParams) =>
    apiClient.get("/restaurants", { params }),

  getRestaurantById: (id: string) => apiClient.get(`/restaurants/${id}`),

  searchRestaurants: (query: string) =>
    apiClient.get("/restaurants/search", { params: { q: query } }),

  getRestaurantsByCategory: (category: string) =>
    apiClient.get(`/restaurants/category/${category}`),

  getRatings: (restaurantId?: string) =>
    restaurantId
      ? apiClient.get(`/restaurants/${restaurantId}/ratings`)
      : apiClient.get("/restaurant/ratings"),

  getReviews: (restaurantId?: string) =>
    restaurantId
      ? apiClient.get(`/restaurants/${restaurantId}/reviews`)
      : apiClient.get("/restaurant/reviews"),

  toggleRestaurantStatus: (isOpen: boolean) =>
    apiClient.post("/restaurant/details/is-open", { isOpen }),

  scheduleAutoOpen: (data: { type: string; scheduledAt?: string }) =>
    apiClient.post("/restaurant/details/schedule-auto-open", data),

  getAnalytics: (
    filter: AnalyticsFilterType,
    extra?: { startDate?: string; endDate?: string; topLimit?: number }
  ) =>
    apiClient.get("/restaurant/dashboard/analytics", {
      params: {
        filter,
        topLimit: extra?.topLimit ?? 5,
        startDate: extra?.startDate,
        endDate: extra?.endDate,
      },
    }),

  getDashboardData: async (period: "today" | "week" | "month") => {
    try {
      const analytics: any = await apiClient.get(
        "/restaurant/dashboard/analytics",
        { params: { filter: period, topLimit: 10 } }
      );
      if (analytics?.status && analytics.data?.summary) {
        return {
          ...analytics,
          data: mapAnalyticsToDashboard(analytics.data),
        };
      }
    } catch (error) {
      console.warn("Analytics endpoint unavailable, using dashboard fallback", error);
    }

    return apiClient.get(`/restaurant/dashboard?period=${period}`);
  },

  getProfile: () => apiClient.get("/restaurant/details"),
};

export const couponService = {
  getCoupons: (page = 1, limit = 20) =>
    apiClient.get("/restaurant/coupons", { params: { page, limit } }),

  createCoupon: (data: CreateCouponRequest) =>
    apiClient.post("/restaurant/coupons", data),

  deactivateCoupon: (id: string) =>
    apiClient.put(`/restaurant/coupons/${id}/deactivate`, {}),
};

export const walletService = {
  getBalance: () => apiClient.get("/restaurant/wallet"),

  getTransactions: (params?: {
    page?: number;
    limit?: number;
    type?: TransactionType;
    from?: string;
    to?: string;
  }) => apiClient.get("/restaurant/wallet/transactions", { params }),

  getPayoutRequests: (params?: {
    page?: number;
    limit?: number;
    status?: PayoutStatusType;
  }) => apiClient.get("/restaurant/wallet/payout-requests", { params }),

  createPayoutRequest: (data: CreatePayoutRequest) =>
    apiClient.post("/restaurant/wallet/payout-request", data),
};

export const staticContentService = {
  getPage: (slug: StaticPageSlug) =>
    apiClient.get(`/user/static-content/${slug}`),
};

// Menu/Food Items API calls
export const foodService = {
  getMenuItems: (restaurantId: string) =>
    apiClient.get(`/restaurants/${restaurantId}/menu`),

  getFoodItemById: (id: string) => apiClient.get(`/food/${id}`),

  searchFood: (query: string) =>
    apiClient.get("/food/search", { params: { q: query } }),

  getFoodByCategory: (restaurantId: string, category: string) =>
    apiClient.get(`/restaurants/${restaurantId}/menu/${category}`),
};

// Order API calls
export const orderService = {
  createOrder: (orderData: OrderData) => apiClient.post("/orders", orderData),

  getOrders: (params?: OrderParams) => apiClient.get("/orders", { params }),

  getOrderById: (id: string) => apiClient.get(`/orders/${id}`),

  updateOrder: (id: string, updateData: any) =>
    apiClient.put(`/orders/${id}`, updateData),

  cancelOrder: (id: string, reason: string) =>
    apiClient.post(`/orders/${id}/cancel`, { reason }),

  getOrderHistory: () => apiClient.get("/orders/history"),

  trackOrder: (id: string) => apiClient.get(`/orders/${id}/track`),
};

// Cart API calls
export const cartService = {
  addToCart: (cartData: CartData) => apiClient.post("/cart", cartData),

  getCart: () => apiClient.get("/cart"),

  updateCartItem: (itemId: string, quantity: number) =>
    apiClient.put(`/cart/${itemId}`, { quantity }),

  removeFromCart: (itemId: string) => apiClient.delete(`/cart/${itemId}`),

  clearCart: () => apiClient.delete("/cart"),

  applyPromo: (promoCode: string) =>
    apiClient.post("/cart/promo", { code: promoCode }),
};

// Payment API calls
export const paymentService = {
  initiatePayment: (paymentData: PaymentData) =>
    apiClient.post("/payments/initiate", paymentData),

  verifyPayment: (paymentId: string, transactionId: string) =>
    apiClient.post(`/payments/${paymentId}/verify`, { transactionId }),

  getPaymentMethods: () => apiClient.get("/payments/methods"),

  savePaymentMethod: (methodData: PaymentMethod) =>
    apiClient.post("/payments/methods", methodData),

  deletePaymentMethod: (methodId: string) =>
    apiClient.delete(`/payments/methods/${methodId}`),
};

// Delivery API calls
export const deliveryService = {
  getDeliveryEstimate: (deliveryData: DeliveryData) =>
    apiClient.post("/delivery/estimate", deliveryData),

  getDeliveryStatus: (orderId: string) =>
    apiClient.get(`/delivery/${orderId}/status`),

  updateDeliveryAddress: (orderId: string, address: string) =>
    apiClient.put(`/delivery/${orderId}/address`, { address }),
};

// Review API calls
export const reviewService = {
  addReview: (reviewData: ReviewData) => apiClient.post("/reviews", reviewData),

  getReviews: (restaurantId: string) =>
    apiClient.get(`/restaurants/${restaurantId}/reviews`),

  deleteReview: (reviewId: string) => apiClient.delete(`/reviews/${reviewId}`),
};

// Favorites API calls
export const favoriteService = {
  addFavorite: (restaurantId: string) =>
    apiClient.post(`/favorites/${restaurantId}`),

  removeFavorite: (restaurantId: string) =>
    apiClient.delete(`/favorites/${restaurantId}`),

  getFavorites: () => apiClient.get("/favorites"),

  isFavorite: (restaurantId: string) =>
    apiClient.get(`/favorites/${restaurantId}/check`),
};

// Kitchen Registration API calls
export const kitchenService = {
  checkRegistrationStatus: () => apiClient.get("/kitchen/registration/status"),

  getRestaurantDetails: () => apiClient.get("/restaurant/details"),

  sendOtp: (phoneNumber: string) =>
    apiClient.post("/restaurant/send-otp", { phoneNumber }),

  verifyOtp: (phoneNumber: string, otp: string) =>
    apiClient.post("/restaurant/verify-otp", { phoneNumber, otp }),

  submitStep1: (data: KitchenRegistrationStep1) =>
    apiClient.post("/restaurant/details/basic-details", data),

  submitStep2: (data: any) =>
    apiClient.post("/restaurant/details/legal-details", data),

  submitStep3: (data: any) =>
    apiClient.post("/restaurant/details/location-details", data),

  submitStep4: (data: any) =>
    apiClient.post("/restaurant/details/operational-details", data),

  submitStep5: (data: any) =>
    apiClient.post("/restaurant/details/delivery-config", data),

  submitStep6: (data: any) =>
    apiClient.post("/restaurant/details/features", data),

  submitStep7: (data: any) =>
    apiClient.post("/restaurant/details/bank-details", data),

  submitStep8: (data: any) => apiClient.post("/restaurant/details/media", data),

  getKitchenProfile: () => apiClient.get("/kitchen/profile"),
};

// categories get apis
export const categoryService = {
  getCategories: () => apiClient.get("/restaurant/category"),
};

export const subCategoryService = {
  getSubCategories: (categoryId: string) => apiClient.get(`/restaurant/subcategory?categoryId=${categoryId}`),
};

// Dish API calls
export const dishService = {
  createDish: (dishData: any) => apiClient.post("/restaurant/dish", dishData),

  updateDish: (dishId: string, dishData: any) =>
    apiClient.put(`/restaurant/dish/${dishId}`, dishData),

  getDishes:() => apiClient.get("/restaurant/dish"),

  getDishById: (dishId: string) => apiClient.get(`/restaurant/dish/${dishId}`),

  deleteDish: (dishId: string) =>
    apiClient.delete(`/restaurant/dish/${dishId}`),

  toggleDishAvailability: (dishId: string, isAvailable: boolean) =>
    apiClient.patch(`/restaurant/dish/${dishId}/availability`, { isAvailable }),
};

// Upload/Media API calls
export const uploadService = {
  uploadImage: (file: File, location: string) => {
    const formData = new FormData();
    formData.append("file", file);

    return apiClient.post(`/upload/${location}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  uploadMultipleImages: (files: File[], location: string) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    return apiClient.post(`/upload/${location}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
};

export const cuisineService = {
  getCuisines: () => apiClient.get("/restaurant/cuisinetype"),
};

export const foodTypeService = {
  getFoodTypes: () => apiClient.get("/restaurant/foodtype"),
};

export const mealTypeService = {
  getMealTypes: () => apiClient.get("/restaurant/mealtype"),
};

export const spiceService = {
  getSpiceLevels: () => apiClient.get("/restaurant/spicelevel"),
};

export const tagsService = {
  getTags: () => apiClient.get("/restaurant/dishtag"),
};

export const allergenService = {
  getAllergens: () => apiClient.get("/restaurant/allergen"),
};

export const dietaryLabelService = {
  getDietaryLabels: () => apiClient.get("/restaurant/dietarylabel"),
};

// Kitchen notification API calls (same contract as the mobile kitchen app)
export const notificationService = {
  getNotifications: (page = 1, limit = 20): Promise<NotificationsListResponse> =>
    apiClient.get("/restaurant/notifications", { params: { page, limit } }),

  getUnreadCount: (): Promise<UnreadCountResponse> =>
    apiClient.get("/restaurant/notifications/unread-count"),

  markAllAsRead: () => apiClient.patch("/restaurant/notifications/read-all", {}),

  markAsRead: (notificationId: string) =>
    apiClient.patch(`/restaurant/notifications/${notificationId}/read`, {}),
};

// Restaurant Order API calls
export const restaurantOrderService = {
  getOrders: (params?: { page?: number; limit?: number; orderStatus?: string }): Promise<OrdersResponse> => 
    apiClient.get("/restaurant/order", { params }),

  getOrderById: (orderId: string) => 
    apiClient.get(`/restaurant/order/${orderId}`),

  updateOrderStatus: (orderId: string, orderStatus: string) => 
    apiClient.patch(`/restaurant/order/${orderId}/status`, { orderStatus }),
};

// Storage utility
export { default as Storage } from './Storage';

// Socket service for real-time communication
export { default as socketService } from './SocketService';
